import {
  AreUsersInLocationsRequest,
  AreUsersInLocationsResponse,
  GetRoleCategoriesByTypeResponse,
  GetUserProfileResponse,
  GetUserRoleAnalyticsRequest,
  GetUserRoleAnalyticsResponse,
  GetUsersInLocationsResponse,
  VerifyAuthRequest,
} from '@weave/schema-gen-ts/dist/schemas/auth-api/v3/auth.pb';
import { QueryKey, UseQueryOptions, useQueries, useQuery } from 'react-query';
import { HttpError } from '@frontend/fetch';
import { Location } from '@frontend/location-helpers';
import { HTTPBoundSchemaMethod, SchemaIO } from '@frontend/schema';
import { useScopedQuery } from '@frontend/scope';
import { Unpromise } from '@frontend/types';
import { UsersTypes } from '@frontend/user-helpers';
import {
  areUsersInLocations,
  getAuthVeify,
  getUserDetails,
  getUserPermissions,
  getUserProfile,
  getUserRoleAnalytics,
  getUsersInLocation,
  getUsersInLocationPaginated,
  list,
  SchemaAuthService,
} from './api';

type SchemaQueryOptions<T extends HTTPBoundSchemaMethod<any>> = UseQueryOptions<
  SchemaIO<T>['output'],
  unknown,
  SchemaIO<T>['output']
>;

type GetLocationUserQueryParams = {
  locationId: string;
  locationType: 'multi' | 'single';
  locationData: Location | null;
  profileId?: string;
};
type GetInviteUserQueryParams = {
  email: string;
  locationId: string;
  locationType: 'multi' | 'single';
  locationData: Location | null;
};
type GetUsersQueryParams = {
  showChildren?: boolean;
};
type GetCreateUserQueryParams = {
  nonceToken: string;
  locationId: string;
};
type GetPasswordPoliciesQueryParams = {
  queryEnabled: boolean;
  locationId: string;
};

export const queryKeys = {
  base: ['users'] as const,
  profile: () => [...queryKeys.base, 'profile'],
  passwordPolicy: () => [...queryKeys.base, 'passwordPolicy'],
  users: () => [...queryKeys.base, `usersList`],
  inviteUserData: (locationId: string) => [...queryKeys.base, `invite-${locationId}`],
  editUserData: (userId: string, locationId: string) => [...queryKeys.base, `edit-${userId}-${locationId}`],
  userDetails: (userId: string) => [...queryKeys.base, `user-details-${userId}`],
  userPermissions: () => [...queryKeys.base, `user-permissions`],
  usersInLocations: (locationIds: string[]) => [...queryKeys.base, 'user-in-locations', locationIds],
  usersInLocationsPaginated: (
    locationIds: string[],
    offset: number,
    limit: number,
    search: string,
    sortBy?: string,
    sortOrder?: string
  ) => [...queryKeys.base, 'user-in-locations-paginated', locationIds, offset, limit, search, sortBy, sortOrder],
  verifyAuth: () => [...queryKeys.base, 'verify-auth'],
  contacts: (userId: string) => [...queryKeys.base, 'contacts', userId],
  getUserRoleAnalytics: ({ locationIds = [], roleIds = [] }: GetUserRoleAnalyticsRequest) => [
    ...queryKeys.base,
    'user-role-analytics',
    ...locationIds,
    ...roleIds,
  ],
  userProfile: (userId: string) => [...queryKeys.base, 'user-profile', userId],
  areUsersInLocations: (userIds: string[], locationIds: string[]) => [
    ...queryKeys.base,
    'are-users-in-locations',
    userIds,
    locationIds,
  ],
};

export const useGetUsers = (parameters: {
  params?: GetUsersQueryParams;
  opts?: UseQueryOptions<Unpromise<ReturnType<typeof list.users>>>;
}) => {
  return useScopedQuery({
    cacheTime: 0,
    queryKey: queryKeys.users(),
    queryFn: () => list.users(parameters.params?.showChildren),
    ...parameters.opts,
  });
};

export const useGetMultiLocationsUsers = ({
  locationIds,
  ...parameters
}: {
  locationIds: string[];
  params?: GetUsersQueryParams;
  opts?: UseQueryOptions<UsersTypes.User[]>;
}) => {
  const { showChildren } = parameters.params || {};

  return useQueries(
    locationIds.map((locationId) => ({
      queryKey: [locationId, ...queryKeys.users()],
      queryFn: () => list.users(showChildren, locationId),
      ...parameters.opts,
    }))
  );
};

export const useGetLocationUser = (params: GetLocationUserQueryParams) => {
  return useScopedQuery({
    retry: 1,
    cacheTime: 0,
    enabled: !!params.profileId && !!params.locationData,
    queryKey: queryKeys.editUserData(params.profileId!, params.locationId),
    queryFn: () =>
      list.userLocationEditData({
        location: params.locationData!,
        profileId: params.profileId!,
        locationType: params.locationType,
      }),
  });
};

export const useGetInviteUserData = (params: GetInviteUserQueryParams) => {
  return useScopedQuery({
    retry: 1,
    enabled: !!params.email.length && !!params.locationData,
    queryKey: queryKeys.inviteUserData(params.locationId),
    queryFn: async () => {
      const profile = await list.userSearch(params.email as string);
      return list.userLocationInviteData({
        location: params.locationData!,
        profile,
        locationType: params.locationType,
      });
    },
  });
};

export const useGetCreateUserData = (
  params: GetCreateUserQueryParams,
  options: { enabled: boolean; onError?: (error: Error) => void }
) => {
  return useQuery({
    retry: 0,
    queryKey: queryKeys.profile(),
    enabled: !!params.nonceToken && !!params.locationId && options.enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: Infinity,
    queryFn: () => list.userNonce({ locationId: params.locationId, nonceToken: params.nonceToken }),
    onError: options.onError,
  });
};

export const useGetPasswordPolicies = (params: GetPasswordPoliciesQueryParams) => {
  return useScopedQuery({
    enabled: params.queryEnabled,
    queryKey: queryKeys.passwordPolicy(),
    queryFn: () => list.passwordPolicies({ locationId: params.locationId }),
  });
};

/**
 * WARN: this is a heavy query. Please use `useGetMultiLocationsUsers` or the `GetUserProfile` endpoint unless you
 * need the additional data returned by `GetUserDetails`
 */
export const useGetUserDetails = (userId: string) => {
  return useQuery({
    enabled: !!userId,
    queryKey: queryKeys.userDetails(userId),
    queryFn: () => getUserDetails(userId),
  });
};

export const useGetUserPermissions = <TData = GetRoleCategoriesByTypeResponse>(
  options?: UseQueryOptions<GetRoleCategoriesByTypeResponse, HttpError, TData>
) => {
  return useScopedQuery({
    queryKey: queryKeys.userPermissions(),
    queryFn: () => getUserPermissions(),
    ...options,
  });
};

/**
 * fetches the entire list of users in selected locations
 * @deprecated use useGetUsersInLocationsPaginated instead so that you don't potentially spend 30s+ downloading hundreds or thousands of users all at once
 * @param locationIds
 * @param options
 * @returns
 */
export const useGetUsersInLocations = <TData = GetUsersInLocationsResponse>(
  locationIds: string[],
  options?: UseQueryOptions<GetUsersInLocationsResponse, HttpError, TData, QueryKey>
) => {
  return useQuery({
    queryKey: queryKeys.usersInLocations(locationIds),
    queryFn: () => {
      return getUsersInLocation(locationIds);
    },
    ...options,
  });
};

export type UserInLocationsSortBy = 'status' | 'firstname' | 'lastname' | 'username';
export type UserInLocationsSortOrder = 'asc' | 'desc' | undefined;

// fetch list of users in selected locations
export const useGetUsersInLocationsPaginated = <TData = GetUsersInLocationsResponse>(
  locationIds: string[],
  search = '',
  offset = 0,
  limit = 40,
  sortBy?: UserInLocationsSortBy,
  sortOrder?: UserInLocationsSortOrder,
  options?: UseQueryOptions<GetUsersInLocationsResponse, HttpError, TData, QueryKey>
) => {
  const searchLower = search.toLowerCase(); // in Safari, it auto capitalizes which can break caching, so always lowercase

  return useQuery({
    queryKey: queryKeys.usersInLocationsPaginated(locationIds, offset, limit, searchLower, sortBy, sortOrder),
    queryFn: () => getUsersInLocationPaginated(locationIds, searchLower, offset, limit, sortBy, sortOrder),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
  });
};

export const useGetVerifyAuth = (payload: VerifyAuthRequest) => {
  return useScopedQuery({
    queryKey: queryKeys.verifyAuth(),
    queryFn: () => getAuthVeify(payload),
  });
};

export const useGetContacts = (
  userId: string,
  opts: SchemaQueryOptions<(typeof SchemaAuthService)['GetContacts']> = {}
) =>
  useQuery({
    queryKey: queryKeys.contacts(userId),
    queryFn: () => SchemaAuthService.GetContacts({ userId }),
    ...opts,
  });

export const useGetUserRoleAnalytics = <TData = GetUserRoleAnalyticsResponse>(
  request: GetUserRoleAnalyticsRequest,
  options?: UseQueryOptions<GetUserRoleAnalyticsResponse, HttpError, TData>
) => {
  return useQuery({
    queryKey: queryKeys.getUserRoleAnalytics(request),
    queryFn: () => getUserRoleAnalytics(request),
    ...options,
    enabled: !!(request.locationIds?.length && request.roleIds?.length) && (options?.enabled ?? true),
  });
};

/**
 * Lightweight endpoint to get basic user profile information.
 * Returns: userId, username, firstName, lastName, type, status, mobileNumber.
 * No ACL permission checks required — recommended over GetUserDetails for most use cases.
 */
export const useGetUserProfile = (
  userId: string,
  options?: UseQueryOptions<GetUserProfileResponse, HttpError, GetUserProfileResponse, QueryKey>
) => {
  return useQuery({
    queryKey: queryKeys.userProfile(userId),
    queryFn: () => getUserProfile({ userId }),
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options,
    enabled: !!userId && (options?.enabled ?? true),
  });
};

/**
 * Fetches multiple user profiles in parallel using individual GetUserProfile calls.
 * Each query is independent — partial failures do not block other results.
 * Max batch size is enforced by the caller, not this hook.
 */
export const useGetUserProfileBatch = (userIds: string[], options?: { enabled?: boolean }) => {
  return useQueries(
    userIds.map((userId) => ({
      queryKey: queryKeys.userProfile(userId),
      queryFn: () => getUserProfile({ userId }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: !!userId && (options?.enabled ?? true),
      retry: 1,
    }))
  );
};

/**
 * Lightweight endpoint for bulk checking user access to locations.
 * Returns a map of locationId → { userIds } indicating which of the requested
 * users have access to each of the requested locations.
 * Only checks users and locations that the caller has access to.
 */
export const useAreUsersInLocations = (
  request: AreUsersInLocationsRequest,
  options?: UseQueryOptions<AreUsersInLocationsResponse, HttpError, AreUsersInLocationsResponse, QueryKey>
) => {
  return useQuery({
    queryKey: queryKeys.areUsersInLocations(request.userIds, request.locationIds),
    queryFn: () => areUsersInLocations(request),
    ...options,
    enabled: !!(request.userIds?.length && request.locationIds?.length) && (options?.enabled ?? true),
  });
};
